"use client";

import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import { useStore } from "jotai";

import { isBookkeepingOutputKey } from "@/components/Spotflow/WorkflowRunner/Outputs/v2FanOut";
import { RunsService, type NodeProgress, type RunIo } from "@/generated";
import { nodeIoFetchPathAtom } from "@/store/editor-v2";
import { DATA_KEYS } from "@/utils/useRemoteData";
import type { NodeBinding } from "./useNodeBindings";

const IO_READY_STATUSES = new Set<NodeProgress["status"]>([
  "succeeded",
  "payment_required",
  "errored",
  "awaiting_human_feedback",
]);

function extractOutputValue(
  outputs: RunIo["outputs"] | undefined,
  outputName: string
): unknown {
  const entry = outputs?.[outputName];
  if (!entry || typeof entry !== "object") return undefined;
  if ("value" in entry) return (entry as { value?: unknown }).value;
  return undefined;
}

export function pickBindingOutputValue(
  outputs: RunIo["outputs"] | undefined,
  outputName: string
): unknown {
  const direct = extractOutputValue(outputs, outputName);
  if (direct !== undefined) return direct;

  if (!outputs) return undefined;
  const keys = Object.keys(outputs).filter((k) => !isBookkeepingOutputKey(k));
  if (keys.length === 0) return undefined;

  const lower = outputName.toLowerCase();
  const caseMatch = keys.find((k) => k.toLowerCase() === lower);
  if (caseMatch) return extractOutputValue(outputs, caseMatch);

  if (keys.length === 1) return extractOutputValue(outputs, keys[0]);
  return undefined;
}

function ioHasOutputs(data: RunIo | undefined): boolean {
  return !!data?.outputs && Object.keys(data.outputs).length > 0;
}

export function resolveSourceRunIoPath(
  sourceNodeId: string,
  getPath: (nodeId: string) => { effectiveNodeId: string; it: number },
  options?: {
    parentSubflowId?: string;
    nodesInSubflow?: string[];
  }
): { effectiveNodeId: string; it: number } {
  const loopAware = getPath(sourceNodeId);
  const it = Math.max(1, loopAware.it);
  const inSameSubflow =
    !!options?.parentSubflowId &&
    !!options.nodesInSubflow?.includes(sourceNodeId);

  if (!inSameSubflow) {
    return { effectiveNodeId: loopAware.effectiveNodeId, it };
  }

  const innerId = loopAware.effectiveNodeId.includes("::")
    ? loopAware.effectiveNodeId
    : sourceNodeId;

  return {
    effectiveNodeId: `${options.parentSubflowId}::${innerId}`,
    it,
  };
}

export function useBindingResolvedValues({
  bindings,
  runId,
  groupId,
  perNodeProgress,
  runIsTerminal,
  parentSubflowId,
  nodesInSubflow,
}: {
  bindings: NodeBinding[];
  runId?: string;
  groupId: string;
  perNodeProgress?: Record<string, NodeProgress | undefined>;
  runIsTerminal?: boolean;
  parentSubflowId?: string;
  nodesInSubflow?: string[];
}): {
  valueByRefId: Record<string, unknown | undefined>;
  isLoading: boolean;
} {
  const store = useStore();

  const uniqueSourceIds = useMemo(
    () => [...new Set(bindings.map((b) => b.sourceNodeId))],
    [bindings]
  );

  const getPath = useMemo(
    () => (nodeId: string) => store.get(nodeIoFetchPathAtom(nodeId)),
    [store]
  );

  const pathOptions = useMemo(
    () => ({ parentSubflowId, nodesInSubflow }),
    [parentSubflowId, nodesInSubflow]
  );

  const queries = useQueries({
    queries: uniqueSourceIds.map((sourceNodeId) => {
      const progress = perNodeProgress?.[sourceNodeId];
      const status = progress?.status;
      const enabled = !!runId && !!status && IO_READY_STATUSES.has(status);
      const { effectiveNodeId, it } = resolveSourceRunIoPath(
        sourceNodeId,
        getPath,
        pathOptions
      );
      const path = {
        group_id: groupId,
        run_id: runId ?? "",
        node_id: effectiveNodeId,
        it,
      };

      return {
        queryKey: [DATA_KEYS.RUN_IO, { path }],
        queryFn: async () => {
          const res = await RunsService.getRunIo({ path });
          return res.data;
        },
        enabled,
        retry: 5,
        retryDelay: 1000,
        refetchOnWindowFocus: false,
        refetchInterval: (query: { state: { data?: RunIo } }) => {
          if (!enabled) return false;
          if (ioHasOutputs(query.state.data)) return false;
          return 1000;
        },
        staleTime: (query: { state: { data?: RunIo } }) => {
          if (runIsTerminal && ioHasOutputs(query.state.data)) return Infinity;
          return 0;
        },
      };
    }),
  });

  const valueByRefId = useMemo(() => {
    const out: Record<string, unknown | undefined> = {};
    const outputsBySource = new Map<string, RunIo["outputs"] | undefined>();

    uniqueSourceIds.forEach((sourceNodeId, index) => {
      outputsBySource.set(sourceNodeId, queries[index]?.data?.outputs);
    });

    for (const binding of bindings) {
      const outputs = outputsBySource.get(binding.sourceNodeId);
      out[binding.refId] = pickBindingOutputValue(outputs, binding.outputName);
    }
    return out;
  }, [bindings, uniqueSourceIds, queries]);

  const isLoading = queries.some(
    (q) => q.isLoading || (q.isFetching && !ioHasOutputs(q.data))
  );

  return { valueByRefId, isLoading };
}
